import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  Stack,
} from "@mui/material";
import {
  Search,
  Download,
  ArrowBack,
  Groups,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import adminService from "../../redux/admin/adminService";
import { colors } from "../../constants/colors";

interface FamilyMemberRow {
  ownerId: number;
  ownerName: string;
  userId: number;
  firstName: string;
  memberPancardNumber: string;
  relation: number;
}

const RELATION_LABELS: Record<number, string> = {
  1: "AUNT",
  2: "BROTHER-IN-LAW",
  3: "BROTHER",
  4: "DAUGHTER",
  5: "DAUGHTER-IN-LAW",
  6: "FATHER",
  7: "FATHER-IN-LAW",
  8: "GRAND DAUGHTER",
  9: "GRAND FATHER",
  10: "GRAND MOTHER",
  11: "GRAND SON",
  12: "MOTHER-IN-LAW",
  13: "MOTHER",
  14: "NEPHEW",
  15: "NIECE",
  16: "SISTER",
  17: "SISTER-IN-LAW",
  18: "SON",
  19: "SON-IN-LAW",
  20: "SPOUSE",
  21: "UNCLE",
  22: "OTHERS",
  23: "COURT APPOINTED LEGAL GUARDIAN",
};

const getRelationLabel = (relation: number) =>
  RELATION_LABELS[relation] || String(relation);

const FamilyData: React.FC = () => {
  const navigate = useNavigate();
  const [dateType, setDateType] = useState<string>("max");
  const [customStartDate, setCustomStartDate] = useState<string>("");
  const [customEndDate, setCustomEndDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [familyMembers, setFamilyMembers] = useState<FamilyMemberRow[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const cacheRef = useRef<Record<string, { totalCount: number; familyMembers: FamilyMemberRow[] }>>({});

  const getDateRange = useCallback(() => {
    const end = new Date();
    const start = new Date();

    switch (dateType) {
      case "1day":
        start.setHours(0, 0, 0, 0);
        break;
      case "7day":
        start.setDate(end.getDate() - 7);
        start.setHours(0, 0, 0, 0);
        break;
      case "1month":
        start.setMonth(end.getMonth() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "1year":
        start.setFullYear(end.getFullYear() - 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "max":
        start.setFullYear(2000, 0, 1);
        start.setHours(0, 0, 0, 0);
        break;
      case "custom":
        if (customStartDate && customEndDate) {
          const customStart = new Date(customStartDate);
          customStart.setHours(0, 0, 0, 0);
          const customEnd = new Date(customEndDate);
          customEnd.setHours(23, 59, 59, 999);
          return { startDate: customStart.getTime(), endDate: customEnd.getTime() };
        }
        return null;
      default:
        start.setFullYear(2000, 0, 1);
        start.setHours(0, 0, 0, 0);
    }

    return { startDate: start.getTime(), endDate: end.getTime() };
  }, [dateType, customStartDate, customEndDate]);

  const fetchData = useCallback(async (isCustomSubmit = false) => {
    if (dateType === "custom" && !isCustomSubmit) return;

    const range = getDateRange();
    if (!range) return;

    const cacheKey = dateType === "custom" ? `custom_${range.startDate}_${range.endDate}` : dateType;

    if (cacheRef.current[cacheKey]) {
      setTotalCount(cacheRef.current[cacheKey].totalCount);
      setFamilyMembers(cacheRef.current[cacheKey].familyMembers);
      return;
    }

    setLoading(true);
    try {
      const response = await adminService.getSuperAdminOpsFamilyData({
        startDate: range.startDate,
        endDate: range.endDate,
      });

      if (response && (response.statusCode === 200 || response.success === 200)) {
        const members = response.data.familyMembers || [];
        const count = Number(response.data.totalCount || members.length);

        setTotalCount(count);
        setFamilyMembers(members);
        cacheRef.current[cacheKey] = { totalCount: count, familyMembers: members };
      } else {
        toast.error(response.message || "Failed to fetch family data");
      }
    } catch (error: any) {
      console.error("Error fetching family data:", error);
      toast.error(error.message || "Failed to load family data");
    } finally {
      setLoading(false);
    }
  }, [getDateRange, dateType]);

  useEffect(() => {
    if (dateType !== "custom") {
      fetchData();
    }
  }, [dateType, fetchData]);

  const handleCustomApply = () => {
    if (!customStartDate || !customEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    fetchData(true);
  };

  const handleExport = () => {
    if (familyMembers.length === 0) {
      toast.error("No data to export");
      return;
    }

    const exportData = familyMembers.map((row) => ({
      "Owner ID": row.ownerId,
      "Owner Name": row.ownerName,
      "Member ID": row.userId,
      "Member Name": row.firstName,
      "Member PAN": row.memberPancardNumber,
      Relation: getRelationLabel(row.relation),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Family Data");
    XLSX.writeFile(workbook, `Family_Data_${dateType}_${Date.now()}.xlsx`);
  };

  const filteredMembers = familyMembers.filter((row) => {
    const query = searchQuery.toLowerCase();
    return (
      String(row.ownerId).includes(query) ||
      (row.ownerName || "").toLowerCase().includes(query) ||
      String(row.userId).includes(query) ||
      (row.firstName || "").toLowerCase().includes(query) ||
      (row.memberPancardNumber || "").toLowerCase().includes(query) ||
      getRelationLabel(row.relation).toLowerCase().includes(query)
    );
  });

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate("/super-admin/ops-dashboard");
            }}
            sx={{ display: "flex", alignItems: "center" }}
          >
            <ArrowBack sx={{ mr: 0.5, fontSize: "inherit" }} />
            Ops Dashboard
          </Link>
          <Typography color="text.primary">Family Data</Typography>
        </Breadcrumbs>

        <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary }}>
          Family Members
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          View active family members and their owners for users created in the selected period.
        </Typography>
      </Box>

      <Card sx={{ mb: 4, borderRadius: "12px", border: "1px solid #e8e8e8", boxShadow: "none" }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center" justifyContent="space-between">
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "text.secondary" }}>
                Select Range Type
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {[
                  { value: "1day", label: "1 Day" },
                  { value: "7day", label: "7 Days" },
                  { value: "1month", label: "1 Month" },
                  { value: "1year", label: "1 Year" },
                  { value: "max", label: "Max" },
                  { value: "custom", label: "Custom Range" },
                ].map((type) => (
                  <Button
                    key={type.value}
                    variant={dateType === type.value ? "contained" : "outlined"}
                    onClick={() => setDateType(type.value)}
                    sx={{
                      borderRadius: "20px",
                      textTransform: "none",
                      px: 3,
                      fontWeight: 600,
                    }}
                  >
                    {type.label}
                  </Button>
                ))}
              </Stack>
            </Grid>

            {dateType === "custom" && (
              <Grid item xs={12} md={5}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                  Custom Range Dates
                </Typography>
                <Stack direction="row" spacing={2}>
                  <TextField
                    type="date"
                    label="Start Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    size="small"
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    size="small"
                  />
                  <Button
                    variant="contained"
                    onClick={handleCustomApply}
                    sx={{ fontWeight: 600, minWidth: "90px" }}
                    disabled={loading || !customStartDate || !customEndDate}
                  >
                    Apply
                  </Button>
                </Stack>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ border: "1px solid #e8e8e8", boxShadow: "none", borderRadius: "12px" }}>
            <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Total Family Members
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, mt: 1, color: colors.primary }}>
                  {loading ? <CircularProgress size={24} /> : totalCount}
                </Typography>
              </Box>
              <Groups sx={{ fontSize: 40, color: colors.primary, opacity: 0.8 }} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper elevation={3} sx={{ borderRadius: "12px", overflow: "hidden", border: "1px solid #e8e8e8", boxShadow: "none" }}>
        <Box sx={{ p: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <TextField
            placeholder="Search by owner, member, PAN, or relation..."
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ width: { xs: "100%", sm: "360px" } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<Download />}
            onClick={handleExport}
            disabled={loading || familyMembers.length === 0}
            sx={{ fontWeight: 600 }}
          >
            Export Excel
          </Button>
        </Box>

        <TableContainer sx={{ maxHeight: "550px" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Owner ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Owner Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Member ID
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Member Name
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Member PAN
                </TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 14, color: "white", backgroundColor: colors.primary }}>
                  Relation
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography mt={2} color="textSecondary">
                      Fetching family records...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredMembers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Typography color="textSecondary" fontSize={15}>
                      No family records found matching your selection.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredMembers.map((row) => (
                  <TableRow key={`${row.ownerId}-${row.userId}-${row.memberPancardNumber}`} hover>
                    <TableCell sx={{ py: 2 }}>{row.ownerId}</TableCell>
                    <TableCell sx={{ py: 2, fontWeight: 600 }}>{row.ownerName}</TableCell>
                    <TableCell sx={{ py: 2 }}>{row.userId}</TableCell>
                    <TableCell sx={{ py: 2, fontWeight: 600 }}>{row.firstName}</TableCell>
                    <TableCell sx={{ py: 2 }}>{row.memberPancardNumber}</TableCell>
                    <TableCell sx={{ py: 2 }}>{getRelationLabel(row.relation)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default FamilyData;
